/**
 * Reservation-intent storage — re-entry continuity for the pre-auth
 * `/reservar` funnel.
 *
 * When an unauthed visitor lands on `/reservar` for a Monthly listing, we
 * snapshot the listing context (config + qty + storage time + display
 * fields) into localStorage. If they leave — bail to WhatsApp, close the
 * tab, get distracted — and come back later (a chatbot "Continuar" link, a
 * bookmark, a re-click), the surfaces that consume this record can offer a
 * "pick up where you left off" path instead of a cold restart at the
 * identity wall.
 *
 * Mirrors [leadFormStorage.ts](./leadFormStorage.ts) exactly — same SSR
 * guard, same 30-day TTL, same try/catch-around-every-access shape. The
 * contact prefill (phone) still lives in `leadFormStorage` and is reused;
 * this module only carries the *listing* the renter was reserving, so a
 * returning visitor can be deep-linked back to the right `/reservar` URL.
 *
 * SSR-safe: every reader / writer guards on `typeof window`. No data
 * leaves the browser — purely a UX shortcut. 30-day TTL matches the
 * contact-prefill window so the two age out together.
 *
 * Consumers:
 *   - `PreAuthReservar` writes the intent on mount and shows a resume
 *     banner when a prior visit is detected.
 *   - `Details/PriceCard` reads the per-config intent to surface a
 *     "Continúa tu reserva" affordance for unauthed returning visitors.
 */

const KEY = 'spotme_reservation_intent_v1'
const TTL_MS = 30 * 24 * 60 * 60 * 1000

export type ReservationIntent = {
  configId: string
  locationId: string
  urlRoute: string | null
  quantity: number
  storageTime: string | null
  listingTitle: string | null
  coverPhotoUrl: string | null
  price: number | null
}

type StoredIntent = ReservationIntent & {
  savedAt: number
}

function isStoredIntent(value: unknown): value is StoredIntent {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v.savedAt === 'number' &&
    typeof v.configId === 'string' &&
    typeof v.locationId === 'string' &&
    typeof v.quantity === 'number'
  )
}

function readStored(): StoredIntent | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!isStoredIntent(parsed)) return null
    if (Date.now() - parsed.savedAt > TTL_MS) {
      window.localStorage.removeItem(KEY)
      return null
    }
    return parsed
  } catch {
    // Malformed JSON, private-browsing exception — treat as no intent.
    return null
  }
}

/** Most recent reservation intent, regardless of which listing. */
export function readReservationIntent(): ReservationIntent | null {
  const stored = readStored()
  if (!stored) return null
  // Build the public shape explicitly rather than destructuring out
  // `savedAt` — this repo's ESLint flags discarded vars even when
  // underscore-prefixed (no `ignoreRestSiblings`).
  return {
    configId: stored.configId,
    locationId: stored.locationId,
    urlRoute: stored.urlRoute,
    quantity: stored.quantity,
    storageTime: stored.storageTime,
    listingTitle: stored.listingTitle,
    coverPhotoUrl: stored.coverPhotoUrl,
    price: stored.price
  }
}

/**
 * Reservation intent scoped to a specific configuration. Returns null when
 * the stored intent is for a different listing — so a resume affordance
 * never shows stale "continue" copy for the wrong space.
 */
export function readReservationIntentForConfig(
  configId: string
): ReservationIntent | null {
  const intent = readReservationIntent()
  if (!intent || intent.configId !== configId) return null
  return intent
}

export function writeReservationIntent(intent: ReservationIntent): void {
  if (typeof window === 'undefined') return
  if (!intent.configId || !intent.locationId) return
  try {
    const payload: StoredIntent = { ...intent, savedAt: Date.now() }
    window.localStorage.setItem(KEY, JSON.stringify(payload))
  } catch {
    // Quota exceeded, private browsing — silently no-op so the page
    // render isn't affected.
  }
}

export function clearReservationIntent(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(KEY)
  } catch {
    // ignore
  }
}
